// ====================================================
// VidyaTrack — Admin: Students Management
// Creates real Firebase Auth + Firestore profiles
// ====================================================

import React, { useState, useEffect } from "react";
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiMail, FiLock, FiUser } from "react-icons/fi";
import { useData } from "../../contexts/DataContext";
import { useAuth } from "../../contexts/AuthContext";
import { useClasses } from "../../hooks/useClasses";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";

const SECTIONS = ["A","B","C","D","E"];

function generatePassword(name) {
  // e.g. Aarav2025@ — easy to share but meets Firebase 6-char minimum
  const first = (name || "Student").split(" ")[0];
  return first.charAt(0).toUpperCase() + first.slice(1,4).toLowerCase() + "2025@";
}

export default function StudentsPage() {
  const { getStudents, updateUser, deleteUser: dbDeleteUser } = useData();
  const { register } = useAuth();
  const { classes } = useClasses();

  const [students,  setStudents]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [filterCls, setFilterCls] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editSt,    setEditSt]    = useState(null);  // null = creating new
  const [saving,    setSaving]    = useState(false);
  const [showPw,    setShowPw]    = useState(false);
  const [errors,    setErrors]    = useState({});

  const blankForm = {
    name:"", email:"", password:"",
    rollNumber:"", classId:"", className:"", section:"",
    phone:"", parentName:"", parentPhone:"",
  };
  const [form, setForm] = useState(blankForm);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setStudents(await getStudents()); }
    catch { toast.error("Failed to load students"); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditSt(null);
    setForm(blankForm);
    setErrors({});
    setShowPw(false);
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditSt(s);
    setForm({
      name:s.name||"", email:s.email||"", password:"",
      rollNumber:s.rollNumber||"", classId:s.classId||"",
      className:s.className||"", section:s.section||"",
      phone:s.phone||"", parentName:s.parentName||"", parentPhone:s.parentPhone||"",
    });
    setErrors({});
    setShowPw(false);
    setShowModal(true);
  };

  // Auto-fill class info when class is picked
  const handleClassChange = (classId) => {
    const cls = classes.find(c => c.id === classId);
    setForm(p => ({
      ...p,
      classId,
      className: cls ? cls.name : "",
      section:   cls ? cls.section : p.section,
    }));
    setErrors(p => ({ ...p, classId:"" }));
  };

  // Auto-generate password when name is entered
  const handleNameChange = (name) => {
    setForm(p => ({
      ...p,
      name,
      password: editSt ? p.password : generatePassword(name),
    }));
    setErrors(p => ({ ...p, name:"" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())   e.name  = "Full name is required";
    if (!form.email.trim())  e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!editSt) {
      if (!form.password)          e.password = "Password is required";
      else if (form.password.length < 6) e.password = "Minimum 6 characters";
    }
    if (!form.rollNumber.trim()) e.rollNumber = "Roll number is required";
    if (!form.classId)           e.classId    = "Please select a class";
    return e;
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (editSt) {
        // Update Firestore profile only (can't change email/pw without re-auth)
        await updateUser(editSt.id, {
          name:        form.name.trim(),
          rollNumber:  form.rollNumber.trim(),
          classId:     form.classId,
          className:   form.className,
          section:     form.section,
          phone:       form.phone.trim(),
          parentName:  form.parentName.trim(),
          parentPhone: form.parentPhone.trim(),
        });
        toast.success(`${form.name}'s profile updated!`);
      } else {
        // Create Firebase Auth + Firestore profile
        await register({
          email:       form.email.trim().toLowerCase(),
          password:    form.password,
          name:        form.name.trim(),
          role:        "student",
          rollNumber:  form.rollNumber.trim(),
          classId:     form.classId,
          className:   form.className,
          section:     form.section,
          phone:       form.phone.trim(),
          parentName:  form.parentName.trim(),
          parentPhone: form.parentPhone.trim(),
        });
        toast.success(
          `✅ Account created!\nEmail: ${form.email}\nPassword: ${form.password}`,
          { autoClose: 8000 }
        );
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.message);
      if (err.message?.includes("email")) setErrors(p => ({ ...p, email: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete ${s.name}?\n\nThis removes their Firestore profile. Their login may still exist in Firebase Auth.`)) return;
    try { await dbDeleteUser(s.id); toast.success("Student deleted!"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const f = (key, val) => { setForm(p => ({...p,[key]:val})); setErrors(p => ({...p,[key]:""})); };

  const classOptions = classes.map(c => ({ id:c.id, label:`${c.name} — Section ${c.section}`, name:c.name, section:c.section }));
  const classLabels  = [...new Set(students.map(s => s.className).filter(Boolean))];

  const filtered = students.filter(s => {
    const matchSearch = (s.name||"").toLowerCase().includes(search.toLowerCase()) ||
      (s.email||"").toLowerCase().includes(search.toLowerCase()) ||
      (s.rollNumber||"").toLowerCase().includes(search.toLowerCase());
    const matchCls = filterCls === "all" || s.className === filterCls || s.classId === filterCls;
    return matchSearch && matchCls;
  });

  return (
    <div className="animate-fade">
      <div className="page-head">
        <div>
          <h1 className="page-title">👨‍🎓 Students</h1>
          <p className="page-subtitle">{students.length} students enrolled</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <FiPlus size={15}/> Add Student
        </button>
      </div>

      {/* Stats */}
      <div className="g g4" style={{ marginBottom:18 }}>
        {[
          { label:"Total Students",  v:students.length,                            c:"var(--blue-700)", bg:"var(--blue-100)" },
          { label:"Classes",         v:[...new Set(students.map(s=>s.classId))].filter(Boolean).length, c:"#7c3aed", bg:"#f5f3ff" },
          { label:"With Roll No.",   v:students.filter(s=>s.rollNumber).length,    c:"var(--green-600)",    bg:"var(--green-50)" },
          { label:"Without Class",   v:students.filter(s=>!s.classId).length,      c:"var(--amber-600)",   bg:"var(--amber-50)" },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding:"14px 16px", textAlign:"center" }}>
            <div style={{ fontSize:"1.6rem", fontWeight:800, color:k.c, fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{k.v}</div>
            <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <div className="search-box" style={{ flex:1, minWidth:200 }}>
          <FiSearch className="search-icon" size={14}/>
          <input placeholder="Search by name, email, roll no…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select className="form-control" style={{ width:"auto" }} value={filterCls} onChange={e=>setFilterCls(e.target.value)}>
          <option value="all">All Classes</option>
          {classLabels.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner"/></div>
        ) : (
          <div className="table-wrap" style={{overflowX:"auto"}}>
            <table className="data-table" style={{minWidth:600}}>
              <thead>
                <tr><th>#</th><th>Student</th><th>Roll No.</th><th>Class</th><th>Email</th><th>Parent</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id}>
                    <td style={{ color:"var(--gray-400)", fontWeight:600 }}>{i+1}</td>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                        <div className="avatar av-sm" style={{ background:`hsl(${i*45},60%,88%)`, color:`hsl(${i*45},60%,30%)`, fontSize:".62rem" }}>
                          {(s.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight:600, fontSize:".88rem" }}>{s.name}</div>
                          {s.phone && <div style={{ fontSize:".72rem", color:"var(--gray-400)" }}>{s.phone}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize:".84rem" }}>{s.rollNumber || <span style={{ color:"var(--gray-400)" }}>—</span>}</td>
                    <td>
                      {s.className
                        ? <span className="badge badge-blue" style={{ fontSize:".68rem" }}>{s.className}{s.section ? ` - ${s.section}` : ""}</span>
                        : <span style={{ color:"var(--gray-400)", fontSize:".8rem" }}>Not set</span>
                      }
                    </td>
                    <td style={{ fontSize:".82rem", color:"var(--gray-500)" }}>{s.email}</td>
                    <td style={{ fontSize:".8rem", color:"var(--gray-500)" }}>
                      {s.parentName || <span style={{ color:"var(--gray-400)" }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display:"flex", gap:5 }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEdit(s)} title="Edit"><FiEdit2 size={13}/></button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(s)} title="Delete"><FiTrash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">👤</div>
                <div className="empty-title">{search || filterCls !== "all" ? "No students match your filter" : "No students yet"}</div>
                <div className="empty-desc">Click "Add Student" to create the first student account.</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editSt ? `Edit — ${editSt.name}` : "Create Student Account"}
        size="lg"
        footer={<>
          <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><div className="spinner spinner-sm"/> {editSt ? "Saving…" : "Creating…"}</> : editSt ? "Save Changes" : "Create Account"}
          </button>
        </>}
      >
        {!editSt && (
          <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:"var(--r8)", padding:"11px 14px", marginBottom:16, fontSize:".83rem", color:"#1d4ed8", display:"flex", gap:8, alignItems:"flex-start" }}>
            <span style={{ flexShrink:0 }}>💡</span>
            <div>
              This creates a <strong>real login account</strong> on Firebase. The student can sign in with the email and password you set here.
              Share these credentials with the student after creating.
            </div>
          </div>
        )}

        <div className="g g2">
          {/* Name */}
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label"><FiUser size={12} style={{ display:"inline", marginRight:4 }}/>Full Name *</label>
            <input className={`form-control${errors.name?" error":""}`} placeholder="Student's full name"
              value={form.name} onChange={e => handleNameChange(e.target.value)}/>
            {errors.name && <div className="form-error">{errors.name}</div>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label"><FiMail size={12} style={{ display:"inline", marginRight:4 }}/>Email Address *</label>
            <input type="email" className={`form-control${errors.email?" error":""}`}
              placeholder="student@email.com"
              value={form.email}
              readOnly={!!editSt}
              style={editSt ? { background:"var(--gray-100)", cursor:"not-allowed" } : {}}
              onChange={e => f("email", e.target.value)}/>
            {errors.email && <div className="form-error">{errors.email}</div>}
            {editSt && <div className="form-hint">Email cannot be changed after creation.</div>}
          </div>

          {/* Password */}
          {!editSt && (
            <div className="form-group">
              <label className="form-label"><FiLock size={12} style={{ display:"inline", marginRight:4 }}/>Password *</label>
              <div className="input-wrap">
                <input
                  type={showPw ? "text" : "password"}
                  className={`form-control pr${errors.password?" error":""}`}
                  placeholder="Min. 6 characters"
                  value={form.password}
                  onChange={e => f("password", e.target.value)}/>
                <button type="button" className="icon-right" onClick={() => setShowPw(v=>!v)}
                  style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", color:"var(--gray-400)" }}>
                  {showPw ? <FiEyeOff size={14}/> : <FiEye size={14}/>}
                </button>
              </div>
              {errors.password && <div className="form-error">{errors.password}</div>}
              <div className="form-hint">Auto-generated: <strong>{form.password}</strong> — you can change it</div>
            </div>
          )}

          {/* Roll Number */}
          <div className="form-group">
            <label className="form-label">Roll Number *</label>
            <input className={`form-control${errors.rollNumber?" error":""}`} placeholder="e.g. 10A-01"
              value={form.rollNumber} onChange={e => f("rollNumber", e.target.value)}/>
            {errors.rollNumber && <div className="form-error">{errors.rollNumber}</div>}
          </div>

          {/* Class */}
          <div className="form-group">
            <label className="form-label">Class *</label>
            <select className={`form-control${errors.classId?" error":""}`}
              value={form.classId} onChange={e => handleClassChange(e.target.value)}>
              <option value="">Select class</option>
              {classOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            {errors.classId && <div className="form-error">{errors.classId}</div>}
            {classes.length === 0 && <div className="form-hint">⚠ No classes found. Add classes first.</div>}
          </div>

          {/* Section (manual override if needed) */}
          {!form.classId && (
            <div className="form-group">
              <label className="form-label">Section</label>
              <select className="form-control" value={form.section} onChange={e => f("section", e.target.value)}>
                <option value="">Select section</option>
                {SECTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          )}

          {/* Phone */}
          <div className="form-group">
            <label className="form-label">Student Phone</label>
            <input className="form-control" placeholder="+977-98XXXXXXXX"
              value={form.phone} onChange={e => f("phone", e.target.value)}/>
          </div>

          {/* Parent info */}
          <div className="form-group">
            <label className="form-label">Parent / Guardian Name</label>
            <input className="form-control" placeholder="Father / Mother name"
              value={form.parentName} onChange={e => f("parentName", e.target.value)}/>
          </div>

          <div className="form-group">
            <label className="form-label">Parent Phone</label>
            <input className="form-control" placeholder="+977-98XXXXXXXX"
              value={form.parentPhone} onChange={e => f("parentPhone", e.target.value)}/>
          </div>
        </div>

        {/* Credentials summary box (new accounts only) */}
        {!editSt && form.name && form.email && form.password && (
          <div style={{ marginTop:8, background:"var(--green-50)", border:"1px solid var(--green-100)", borderRadius:"var(--r8)", padding:"12px 14px" }}>
            <div style={{ fontSize:".78rem", fontWeight:700, color:"var(--green-600)", marginBottom:6 }}>📋 Credentials to share with student:</div>
            <div style={{ fontSize:".82rem", fontFamily:"monospace", lineHeight:1.8, color:"var(--gray-700)" }}>
              <div>🌐 URL: <strong>vidyatrack3.web.app</strong></div>
              <div>📧 Email: <strong>{form.email}</strong></div>
              <div>🔑 Password: <strong>{form.password}</strong></div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
